import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft,
  CircleNotch,
  Plus,
  Trash,
  CheckSquare,
  Square,
  MapPin,
  Clock,
  User,
  Camera,
  Package,
  Play,
  CheckCircle,
  CalendarBlank,
  PaperPlaneTilt
} from '@phosphor-icons/react';


const STATUS_CONFIG = {
  'scheduled': { label: 'Programada', color: 'bg-blue-100 text-blue-800' },
  'in_progress': { label: 'En Progreso', color: 'bg-yellow-100 text-yellow-800' },
  'completed': { label: 'Completada', color: 'bg-green-100 text-green-800' },
  'cancelled': { label: 'Cancelada', color: 'bg-red-100 text-red-800' },
};

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baja' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export default function WorkOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [workOrder, setWorkOrder] = useState({
    client_id: '',
    title: '',
    description: '',
    project_address: '',
    project_city: '',
    project_state: '',
    project_zip: '',
    scheduled_date: '',
    scheduled_time: '09:00',
    estimated_hours: '',
    assigned_technicians: [],
    checklist: [],
    priority: 'normal',
    notes: ''
  });
  const [clients, setClients] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchTechnicians();
    if (!isNew) {
      fetchWorkOrder();
    }
  }, [id, isNew]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/technicians/available`, { withCredentials: true });
      setTechnicians(response.data);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    }
  };

  const fetchWorkOrder = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/work-orders/${id}`, { withCredentials: true });
      setWorkOrder(response.data);
    } catch (error) {
      console.error('Error fetching work order:', error);
      navigate('/work-orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!workOrder.client_id || !workOrder.title || !workOrder.scheduled_date) {
      alert('Completa los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const response = await axios.post(`${API_URL}/api/work-orders`, workOrder, { withCredentials: true });
        navigate(`/work-orders/${response.data.id}`);
      } else {
        await axios.put(`${API_URL}/api/work-orders/${id}`, workOrder, { withCredentials: true });
        setEditing(false);
        fetchWorkOrder();
      }
    } catch (error) {
      console.error('Error saving work order:', error);
      alert('Error al guardar la orden de trabajo');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleChecklist = async (itemId) => {
    try {
      await axios.post(`${API_URL}/api/work-orders/${id}/checklist/${itemId}/toggle`, {}, { withCredentials: true });
      fetchWorkOrder();
    } catch (error) {
      console.error('Error toggling checklist:', error);
    }
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    setWorkOrder({
      ...workOrder,
      checklist: [...workOrder.checklist, { description: newChecklistItem, completed: false }]
    });
    setNewChecklistItem('');
  };

  const removeChecklistItem = (index) => {
    const newChecklist = workOrder.checklist.filter((_, i) => i !== index);
    setWorkOrder({ ...workOrder, checklist: newChecklist });
  };

  const handleTechnicianToggle = (techId) => {
    const current = workOrder.assigned_technicians || [];
    if (current.includes(techId)) {
      setWorkOrder({ ...workOrder, assigned_technicians: current.filter(id => id !== techId) });
    } else {
      setWorkOrder({ ...workOrder, assigned_technicians: [...current, techId] });
    }
  };

  const handleStartWork = async () => {
    try {
      await axios.put(`${API_URL}/api/work-orders/${id}`, { status: 'in_progress' }, { withCredentials: true });
      fetchWorkOrder();
    } catch (error) {
      console.error('Error starting work:', error);
    }
  };

  const handleCompleteWork = async () => {
    if (!window.confirm('¿Confirmas que la orden de trabajo está completada?')) return;
    try {
      await axios.post(`${API_URL}/api/work-orders/${id}/complete`, { notes: '' }, { withCredentials: true });
      fetchWorkOrder();
    } catch (error) {
      console.error('Error completing work:', error);
    }
  };

  const handleSendToTechnician = async () => {
    if (!workOrder.assigned_technicians?.length) {
      alert('Primero debes asignar tecnicos a esta orden de trabajo');
      return;
    }
    if (!window.confirm('¿Enviar la orden de trabajo por email a los tecnicos asignados?')) return;
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API_URL}/api/work-orders/${id}/send-to-technician`, {}, { withCredentials: true });
      alert(`Enviado exitosamente a: ${response.data.sent_to?.join(', ')}\nAdjuntos: ${response.data.attachments_count} archivo(s)`);
      fetchWorkOrder();
    } catch (error) {
      console.error('Error sending to technician:', error);
      alert(error.response?.data?.detail || 'Error al enviar email al tecnico');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG['scheduled'];
    return (
      <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="work-order-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/work-orders')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? 'Nueva Orden de Trabajo' : editing ? 'Editar Orden' : workOrder.work_order_number}
          </h1>
          {!isNew && !editing && (
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={workOrder.status} />
              <span className="text-slate-500">• {workOrder.client_name}</span>
              {workOrder.auto_generated && (
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">Auto-generada</span>
              )}
            </div>
          )}
        </div>
        {!isNew && !editing && (
          <div className="flex items-center gap-2 flex-wrap">
            {workOrder.assigned_technicians?.length > 0 && workOrder.status !== 'completed' && (
              <button
                onClick={handleSendToTechnician}
                disabled={sendingEmail}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                data-testid="send-to-technician-button"
              >
                {sendingEmail ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} />}
                {sendingEmail ? 'Enviando...' : 'Enviar al Tecnico'}
              </button>
            )}
            {workOrder.status === 'scheduled' && (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Editar
                </button>
                <button
                  onClick={handleStartWork}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                  data-testid="start-work-button"
                >
                  <Play size={18} weight="fill" />
                  Iniciar Trabajo
                </button>
              </>
            )}
            {workOrder.status === 'in_progress' && (
              <button
                onClick={handleCompleteWork}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                data-testid="complete-work-button"
              >
                <CheckCircle size={18} weight="fill" />
                Completar
              </button>
            )}
          </div>
        )}
      </div>

      {editing ? (
        /* Edit Form */
        <form onSubmit={handleSave} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Información Básica</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Cliente *</label>
                <select
                  value={workOrder.client_id}
                  onChange={(e) => setWorkOrder({ ...workOrder, client_id: e.target.value })}
                  className="form-input"
                  required
                  data-testid="client-select"
                >
                  <option value="">Seleccionar cliente...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Prioridad</label>
                <select
                  value={workOrder.priority}
                  onChange={(e) => setWorkOrder({ ...workOrder, priority: e.target.value })}
                  className="form-input"
                >
                  {PRIORITY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Título *</label>
                <input
                  type="text"
                  value={workOrder.title}
                  onChange={(e) => setWorkOrder({ ...workOrder, title: e.target.value })}
                  className="form-input"
                  placeholder="ej: Instalación de cámaras CCTV"
                  required
                  data-testid="title-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Descripción</label>
                <textarea
                  value={workOrder.description || ''}
                  onChange={(e) => setWorkOrder({ ...workOrder, description: e.target.value })}
                  className="form-input"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Ubicación del Proyecto</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="form-label">Dirección *</label>
                <input
                  type="text"
                  value={workOrder.project_address}
                  onChange={(e) => setWorkOrder({ ...workOrder, project_address: e.target.value })}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Ciudad</label>
                <input
                  type="text"
                  value={workOrder.project_city || ''}
                  onChange={(e) => setWorkOrder({ ...workOrder, project_city: e.target.value })}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Estado</label>
                <input
                  type="text"
                  value={workOrder.project_state || ''}
                  onChange={(e) => setWorkOrder({ ...workOrder, project_state: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Programación</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Fecha *</label>
                <input
                  type="date"
                  value={workOrder.scheduled_date}
                  onChange={(e) => setWorkOrder({ ...workOrder, scheduled_date: e.target.value })}
                  className="form-input"
                  required
                  data-testid="date-input"
                />
              </div>
              <div>
                <label className="form-label">Hora</label>
                <input
                  type="time"
                  value={workOrder.scheduled_time || '09:00'}
                  onChange={(e) => setWorkOrder({ ...workOrder, scheduled_time: e.target.value })}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Horas Estimadas</label>
                <input
                  type="number"
                  value={workOrder.estimated_hours || ''}
                  onChange={(e) => setWorkOrder({ ...workOrder, estimated_hours: parseFloat(e.target.value) || '' })}
                  className="form-input"
                  step="0.5"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Technicians */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Técnicos Asignados</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {technicians.map(tech => (
                <label
                  key={tech.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    workOrder.assigned_technicians?.includes(tech.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={workOrder.assigned_technicians?.includes(tech.id)}
                    onChange={() => handleTechnicianToggle(tech.id)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-sm">{tech.name}</p>
                    <p className="text-xs text-slate-500">{tech.role}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Checklist */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Checklist de Tareas</h2>
            <div className="space-y-3 mb-4">
              {workOrder.checklist?.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <span className="flex-1">{item.description}</span>
                  <button
                    type="button"
                    onClick={() => removeChecklistItem(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newChecklistItem}
                onChange={(e) => setNewChecklistItem(e.target.value)}
                className="form-input flex-1"
                placeholder="Nueva tarea..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
              />
              <button
                type="button"
                onClick={addChecklistItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => isNew ? navigate('/work-orders') : setEditing(false)}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              data-testid="save-button"
            >
              {saving && <CircleNotch size={18} className="animate-spin" />}
              {saving ? 'Guardando...' : 'Guardar Orden'}
            </button>
          </div>
        </form>
      ) : (
        /* View Mode */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Details */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold mb-4">{workOrder.title}</h2>
              {workOrder.description && (
                <p className="text-slate-600 mb-4">{workOrder.description}</p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <CalendarBlank size={20} className="text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Fecha</p>
                    <p className="font-medium">
                      {new Date(workOrder.scheduled_date).toLocaleDateString('es-ES')}
                      {workOrder.scheduled_time && ` • ${workOrder.scheduled_time}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Clock size={20} className="text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Horas</p>
                    <p className="font-medium">
                      {workOrder.actual_hours || 0} / {workOrder.estimated_hours || '-'} hrs
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg col-span-2">
                  <MapPin size={20} className="text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Ubicación</p>
                    <p className="font-medium">
                      {workOrder.project_address}
                      {workOrder.project_city && `, ${workOrder.project_city}`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Checklist */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Checklist de Tareas</h2>
              {workOrder.checklist?.length > 0 ? (
                <div className="space-y-2">
                  {workOrder.checklist.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => workOrder.status === 'in_progress' && handleToggleChecklist(item.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        item.completed ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'
                      } ${workOrder.status === 'in_progress' ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                    >
                      {item.completed ? (
                        <CheckSquare size={22} className="text-green-600" weight="fill" />
                      ) : (
                        <Square size={22} className="text-slate-400" />
                      )}
                      <span className={item.completed ? 'line-through text-slate-500' : ''}>
                        {item.description}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No hay tareas en el checklist</p>
              )}
            </div>

            {/* Materials Used */}
            {workOrder.materials_used?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold mb-4">Materiales Utilizados</h2>
                <div className="space-y-2">
                  {workOrder.materials_used.map((material) => (
                    <div key={material.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Package size={20} className="text-slate-400" />
                        <div>
                          <p className="font-medium">{material.name}</p>
                          <p className="text-sm text-slate-500">{material.quantity} {material.unit}</p>
                        </div>
                      </div>
                      <span className="font-semibold">${material.total_cost?.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-3 border-t border-slate-200 font-semibold">
                    <span>Total Materiales</span>
                    <span>${workOrder.total_material_cost?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Client Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold mb-3">Cliente</h3>
              <Link to={`/clients/${workOrder.client_id}`} className="text-blue-600 hover:text-blue-700 font-medium">
                {workOrder.client_name}
              </Link>
              {workOrder.client_phone && (
                <p className="text-sm text-slate-500 mt-1">{workOrder.client_phone}</p>
              )}
            </div>

            {/* Assigned Technicians */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold mb-3">Técnicos Asignados</h3>
              {workOrder.technician_names?.length > 0 ? (
                <div className="space-y-2">
                  {workOrder.technician_names.map((name, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <User size={16} className="text-slate-400" />
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Sin técnicos asignados</p>
              )}
              {workOrder.sent_to_technicians && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="flex items-center gap-2 text-green-600 text-sm" data-testid="sent-to-tech-indicator">
                    <PaperPlaneTilt size={14} weight="fill" />
                    <span>Enviado por email</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {workOrder.sent_to_technicians_at && new Date(workOrder.sent_to_technicians_at).toLocaleString('es-ES')}
                  </p>
                </div>
              )}
            </div>

            {/* Quote Link */}
            {workOrder.quote_id && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold mb-3">Cotización Relacionada</h3>
                <Link
                  to={`/quotes/${workOrder.quote_id}`}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Ver cotización
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
